import { api } from "../api/api";

// Offline-Unterstützung für die Fahrerseite: Tourdaten werden bei jedem
// erfolgreichen Laden lokal zwischengespeichert, Aktionen (Zugestellt/Leergut)
// landen in einer persistenten Warteschlange und werden automatisch
// nachgeholt, sobald wieder eine Verbindung besteht. Bewusst nur localStorage
// (kein Service Worker/App-Shell-Caching) – siehe Projektplan.

const TOUREN_KEY   = "mh_fahrer_touren";
const LIEF_PREFIX  = "mh_fahrer_lief_";
const QUEUE_KEY     = "mh_fahrer_queue";
const MAX_CACHE_AGE_MS = 3 * 24 * 60 * 60 * 1000; // 3 Tage
const CHANGE_EVENT  = "mh-fahrer-queue-changed";
const ACTION_FAILED_EVENT = "mh-fahrer-action-failed";
const SYNC_INTERVAL_MS = 20000;
const REQUEST_TIMEOUT_MS = 6000;

function safeGet(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function pruneOldLieferungenCache() {
  const now = Date.now();
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(LIEF_PREFIX)) continue;
    const entry = safeGet(key);
    if (!entry || now - entry.ts > MAX_CACHE_AGE_MS) {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
    }
  }
}

export function cacheTouren(list) {
  safeSet(TOUREN_KEY, { data: list, ts: Date.now() });
}

export function getCachedTouren() {
  return safeGet(TOUREN_KEY);
}

export function cacheLieferungen(tourId, datum, list) {
  pruneOldLieferungenCache();
  safeSet(`${LIEF_PREFIX}${tourId}_${datum}`, { data: list, ts: Date.now() });
}

export function getCachedLieferungen(tourId, datum) {
  return safeGet(`${LIEF_PREFIX}${tourId}_${datum}`);
}

// ── Warteschlange ──────────────────────────────────────────
export function getQueue() {
  return safeGet(QUEUE_KEY) ?? [];
}

function saveQueue(queue) {
  safeSet(QUEUE_KEY, queue);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

function makeId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function enqueue(action) {
  const queue = getQueue();
  queue.push({ id: makeId(), ts: Date.now(), ...action });
  saveQueue(queue);
  trySync();
}

export function onQueueChange(fn) {
  const handler = () => fn(getQueue());
  window.addEventListener(CHANGE_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}

// Wird ausgelöst, wenn eine Aktion endgültig (nicht nur wegen fehlendem Netz)
// vom Server abgelehnt wurde, damit die UI die optimistische Änderung zurückrollen kann.
export function onActionFailed(fn) {
  const handler = (e) => fn(e.detail);
  window.addEventListener(ACTION_FAILED_EVENT, handler);
  return () => window.removeEventListener(ACTION_FAILED_EVENT, handler);
}

function isNetworkError(err) {
  return err instanceof TypeError || err?.name === "AbortError" || /failed to fetch|networkerror|load failed/i.test(err?.message || "");
}

async function sendAction(item) {
  if (item.type === "geliefert") {
    return api.fahrer.geliefert(item.bestellungId, item.datum, REQUEST_TIMEOUT_MS);
  }
  if (item.type === "leergut") {
    return api.fahrer.leergut(item.bestellungId, item.datum, item.payload.abgeholt, REQUEST_TIMEOUT_MS);
  }
  throw new Error(`Unbekannter Aktionstyp: ${item.type}`);
}

let syncing = false;

export async function trySync() {
  if (syncing || !navigator.onLine) return;
  syncing = true;
  try {
    let queue = getQueue();
    while (queue.length > 0) {
      const item = queue[0];
      try {
        await sendAction(item);
        queue = queue.slice(1);
        saveQueue(queue);
      } catch (err) {
        if (isNetworkError(err)) break; // weiterhin offline – Rest für später aufheben
        // Serverseitige Ablehnung (z.B. Bestellung gelöscht) – verwerfen statt endlos zu wiederholen,
        // UI per Event benachrichtigen, damit die optimistische Änderung zurückgerollt werden kann.
        console.error("[fahrerOffline] Aktion verworfen:", item, err);
        queue = queue.slice(1);
        saveQueue(queue);
        window.dispatchEvent(new CustomEvent(ACTION_FAILED_EVENT, { detail: { ...item, error: err.message } }));
      }
    }
  } finally {
    syncing = false;
  }
}

let autoSyncStarted = false;

export function startAutoSync() {
  if (autoSyncStarted) return;
  autoSyncStarted = true;
  window.addEventListener("online", trySync);
  setInterval(trySync, SYNC_INTERVAL_MS);
  trySync();
}
